import { SUBMISSION_ID_KEY, EDITED_AT_KEY } from './submissionId';

function getQueueKey(eventPath) {
    return `scout_queue_${eventPath || 'GLOBAL'}`;
}

/** @returns {boolean} false when nothing was added - no record, or that match is already queued */
export function enqueueRecord(dataObject, gamemode, eventPath) {
    return appendRecord(getQueueKey(eventPath), dataObject, gamemode);
}

/**
 * Timekeeper timestamps, queued for the Octopus Pi and for the Relay tab's Upload Queue.
 *
 * Kept out of the scouting queue on purpose: a timestamp entry filed as normal_data
 * would be read by Neptune as a scout's match record. The Relay tab uploads this queue
 * to timekeeper_data instead - the map Timekeeper's own Upload button writes - so
 * timestamps taken offline reach Firebase the same way matches do.
 */
function getTimekeeperQueueKey(eventPath) {
    return `timekeeper_queue_${eventPath || 'GLOBAL'}`;
}

export function enqueueTimekeeper(dataObject, gamemode, eventPath) {
    return appendRecord(getTimekeeperQueueKey(eventPath), dataObject, gamemode);
}

export function getTimekeeperQueue(eventPath) {
    try {
        return JSON.parse(localStorage.getItem(getTimekeeperQueueKey(eventPath)) || '[]');
    } catch {
        return [];
    }
}

/**
 * Fix the id and capture time once, here - a retry must reuse them, never re-mint.
 *
 * A match from Scout.js or a scanned QR carries its id as `submission_id`
 * (lib/submissionId.js); it becomes the record's `id` and leaves the data, so queued
 * records keep the shape the Pi, the Relay tab and USB transfers expect. A record
 * without one gets a fresh id, as before. A match already in the queue - the same QR
 * scanned twice - is not added again.
 */
function appendRecord(key, dataObject, gamemode) {
    if (!dataObject || dataObject === false) return false;
    let queue;
    try {
        queue = JSON.parse(localStorage.getItem(key) || '[]');
    } catch {
        queue = [];
    }
    const { [SUBMISSION_ID_KEY]: submissionId, [EDITED_AT_KEY]: editedAt, ...data } = dataObject;
    const id = submissionId || crypto.randomUUID();
    if (queue.some(record => record.id === id)) return false;
    queue.push({
        ...data,
        id,
        _queued_at: Date.now(),
        _gamemode: gamemode,
        // An edited copy (the Scanner's text box) stays marked as one: it may overwrite
        // Firebase, and reaches the Pi as a new version.
        ...(editedAt ? { _edited_at: Number(editedAt) } : {}),
    });
    localStorage.setItem(key, JSON.stringify(queue));
    return true;
}

export function getQueue(eventPath) {
    try {
        return JSON.parse(localStorage.getItem(getQueueKey(eventPath)) || '[]');
    } catch {
        return [];
    }
}

/**
 * Stack imported records (several files, several drives) onto the queue, one per id.
 * The same record seen twice - in two files, or on two drives - is kept once; a copy
 * edited later wins (an edit is a correction) and is owed to Firebase again.
 * @returns {number} records added (corrections replace, so they are not counted)
 */
export function mergeIntoQueue(incomingRecords, eventPath) {
    if (!Array.isArray(incomingRecords)) return 0;
    const queue = getQueue(eventPath);
    const index = new Map(queue.map((r, i) => [r.id, i]));
    const corrected = [];
    let added = 0;
    for (const record of incomingRecords) {
        if (!record || !record.id) continue;
        const at = index.get(record.id);
        if (at === undefined) {
            index.set(record.id, queue.length);
            queue.push(record);
            added++;
        } else if ((Number(record._edited_at) || 0) > (Number(queue[at]._edited_at) || 0)) {
            queue[at] = record;
            corrected.push(record.id);
        }
    }
    localStorage.setItem(getQueueKey(eventPath), JSON.stringify(queue));
    if (corrected.length) unmarkFirebaseUploaded(corrected, eventPath);
    return added;
}

// --- one function per job, for either queue -------------------------------------
//
// The Relay tab shows and uploads both queues: scouting (normal_data) and Timekeeper
// (timekeeper_data). `stream` picks which; it defaults to scouting, so every existing
// caller is unchanged.

/** The two queues, by the name the Octopus uploader also uses for them (base_name). */
export const STREAMS = ['scouting', 'timekeeper'];

function streamKey(eventPath, stream = 'scouting') {
    if (stream === 'timekeeper') return getTimekeeperQueueKey(eventPath);
    if (stream === 'scouting') return getQueueKey(eventPath);
    throw new Error(`unknown queue: ${stream}`);
}

export function getStreamQueue(eventPath, stream = 'scouting') {
    try {
        return JSON.parse(localStorage.getItem(streamKey(eventPath, stream)) || '[]');
    } catch {
        return [];
    }
}

/** Only for a deliberate delete (the Relay tab's × or Clear Queue) - nothing else removes. */
export function removeFromQueue(ids, eventPath, stream = 'scouting') {
    const idSet = new Set(ids);
    const queue = getStreamQueue(eventPath, stream).filter(r => !idSet.has(r.id));
    localStorage.setItem(streamKey(eventPath, stream), JSON.stringify(queue));
    unmarkFirebaseUploaded(ids, eventPath);
}

export function clearQueue(eventPath, stream = 'scouting') {
    const ids = getStreamQueue(eventPath, stream).map(r => r.id);
    localStorage.removeItem(streamKey(eventPath, stream));
    // Only this queue's marks: the other queue's share the list.
    unmarkFirebaseUploaded(ids, eventPath);
}

/**
 * An edited record is owed to Firebase again, so the fix goes up on the next upload - and
 * stamped with when it was edited, which lets it overwrite Firebase (an edit is a
 * correction) and makes it a new version for the Pi (octopus.js piKey).
 */
export function updateRecord(id, changes, eventPath, stream = 'scouting', editedAt = Date.now()) {
    const queue = getStreamQueue(eventPath, stream).map(r => r.id === id ? { ...r, ...changes, _edited_at: editedAt } : r);
    localStorage.setItem(streamKey(eventPath, stream), JSON.stringify(queue));
    unmarkFirebaseUploaded([id], eventPath);
}

/**
 * Queue an edited copy of a match: replaces the queued record with the same id if there is
 * one, else adds it. @returns {'updated'|'added'|false}
 */
export function queueEditedRecord(dataObject, gamemode, eventPath, stream = 'scouting') {
    if (!dataObject) return false;
    const { [SUBMISSION_ID_KEY]: id, [EDITED_AT_KEY]: editedAt, ...data } = dataObject;
    const when = Number(editedAt) || Date.now();
    if (id && getStreamQueue(eventPath, stream).some(r => r.id === id)) {
        updateRecord(id, data, eventPath, stream, when);
        return 'updated';
    }
    const added = appendRecord(streamKey(eventPath, stream), { ...dataObject, [EDITED_AT_KEY]: when }, gamemode);
    return added ? 'added' : false;
}

// --- what the Relay tab has uploaded to Firebase --------------------------------
//
// Uploading no longer removes a record from the queue: the Octopus uploader reads the
// same queue, and a record removed before the Pi had it never reached the Pi. So the
// Relay tab keeps the ids it uploaded here instead, and its next upload sends only
// records not in this list - new ones, and ones edited since. One list per event for
// both queues: ids are unique across them.

function getFirebaseUploadedKey(eventPath) {
    return `firebase_uploaded_${eventPath || 'GLOBAL'}`;
}

export function getFirebaseUploadedIds(eventPath) {
    try {
        const raw = JSON.parse(localStorage.getItem(getFirebaseUploadedKey(eventPath)) || '[]');
        return new Set(Array.isArray(raw) ? raw : []);
    } catch {
        return new Set();
    }
}

function writeFirebaseUploadedIds(eventPath, set) {
    localStorage.setItem(getFirebaseUploadedKey(eventPath), JSON.stringify([...set]));
}

export function markFirebaseUploaded(ids, eventPath) {
    if (!ids.length) return;
    const set = getFirebaseUploadedIds(eventPath);
    ids.forEach(id => set.add(id));
    writeFirebaseUploadedIds(eventPath, set);
}

export function unmarkFirebaseUploaded(ids, eventPath) {
    const set = getFirebaseUploadedIds(eventPath);
    let changed = false;
    ids.forEach(id => { changed = set.delete(id) || changed; });
    if (changed) writeFirebaseUploadedIds(eventPath, set);
}

/** Queued records the Relay tab has not uploaded to Firebase yet, from one queue. */
export function pendingForFirebase(eventPath, stream = 'scouting') {
    const uploaded = getFirebaseUploadedIds(eventPath);
    return getStreamQueue(eventPath, stream).filter(r => !uploaded.has(r.id));
}

export async function exportQueue(eventPath) {
    const data = localStorage.getItem(getQueueKey(eventPath)) || '[]';
    const filename = `scout-queue-${eventPath || 'GLOBAL'}-${crypto.randomUUID()}.json`;

    // Chrome/Edge: open "Save As" dialog so user can choose USB drive directly
    if (window.showSaveFilePicker) {
        try {
            const handle = await window.showSaveFilePicker({
                suggestedName: filename,
                types: [{ description: 'JSON', accept: { 'application/json': ['.json'] } }]
            });
            const writable = await handle.createWritable();
            await writable.write(data);
            await writable.close();
            return true;
        } catch (err) {
            if (err.name === 'AbortError') return false; // user cancelled — silent
            // any other error falls through to download fallback
        }
    }

    // Fallback for Safari / Firefox: standard browser download
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    return true;
}

export function getMatchTypeLabel(gamemode) {
    const labels = ['Prescouting', 'Practice', 'Quals', 'Elims'];
    return labels[gamemode] ?? 'Unknown';
}

export function getPositionLabel(allianceColor, teamPosition, gamemode) {
    const alliance = String(allianceColor || '').toUpperCase();
    const pos = parseInt(teamPosition);
    if (gamemode === 3) {
        const elimLabels = ['Captain', 'Pick 1', 'Pick 2'];
        const label = elimLabels[pos - 1] ?? String(pos);
        return `${alliance} ${label}`;
    }
    return `${alliance}${pos}`;
}
